import { useState, useCallback, useEffect } from "react";
import apiService from "~/services/service";
import { API_ROUTES } from "~/api-routes";
import API_ROUTES_V2 from "~/api-routes-v2";

// Base32 alphabet for ULID conversion
const base32 = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/**
 * Convert ULID to UUID
 */
const ulidToUuid = (ulidStr) => {
  if (ulidStr.length !== 26) throw new Error("Invalid ULID");

  function decodeBase32(str) {
    let num = 0;
    for (let i = 0; i < str.length; i++) {
      num = num * 32 + base32.indexOf(str[i]);
    }
    return num;
  }

  const timestamp = decodeBase32(ulidStr.slice(0, 10));
  const randPart = ulidStr.slice(10);
  let randNum = BigInt(0);
  for (let i = 0; i < randPart.length; i++) {
    randNum = randNum * BigInt(32) + BigInt(base32.indexOf(randPart[i]));
  }

  const tsHex = timestamp.toString(16).padStart(12, "0");
  const randHex = randNum.toString(16).padStart(20, "0").slice(0, 20);
  const hex = tsHex + randHex;

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join("-");
};

/**
 * Generates breadcrumb string based on metadata and fetched data
 * Format: "Core Entity Information / Entity Information / [Officer/Shareholder] / name"
 * For payslip: "Payroll / [year] / [payslip_name]"
 * For ad hoc request: "Ad Hoc Requests / [year] / [reason] / Request [request_id]"
 */
const generateBreadcrumb = (item) => {
  const breadcrumbParts = [];

  console.log("Item:.......", item);
  // Add officer breadcrumb with core entity info
  if (item.officer_data?.data?.name && item.core_entity_info) {
    breadcrumbParts.push("Core Entity Information");
    breadcrumbParts.push("Entity Information");
    breadcrumbParts.push("Officer");
    breadcrumbParts.push(item.officer_data.data.name);
  }
  // Add officer breadcrumb without core entity info
  else if (item.officer_data?.data) {
    console.log("Officer data 123:", item.officer_data.data.type);
    breadcrumbParts.push("Core Entity Information");
    if (item.officer_data.data.type === "officer") {
      breadcrumbParts.push("Officer");
    } else {
      breadcrumbParts.push("Power of Attorney");
    }
    if (item.officer_data.data.name) {
      breadcrumbParts.push(item.officer_data.data.name);
    }
  }
  // Add shareholder breadcrumb with core entity info
  else if (item.shareHolder_data?.data && item.core_entity_info) {
    breadcrumbParts.push("Core Entity Information");
    breadcrumbParts.push("Entity Information");
    breadcrumbParts.push("Shareholder");
    if (item.shareHolder_data.data.legal_name) {
      breadcrumbParts.push(item.shareHolder_data.data.legal_name);
    }
    if (item.shareHolder_data.data.name) {
      breadcrumbParts.push(item.shareHolder_data.data.name);
    }
  }
  // Add shareholder breadcrumb without core entity info
  else if (item.shareHolder_data?.data) {
    breadcrumbParts.push("Core Entity Information");
    breadcrumbParts.push("Shareholder");

    if (item.shareHolder_data.data.legal_name) {
      breadcrumbParts.push(item.shareHolder_data.data.legal_name);
    }
    if (item.shareHolder_data.data.name) {
      breadcrumbParts.push(item.shareHolder_data.data.name);
    }
  }
  // Add director breadcrumb with core entity info
  else if (item.director_data?.data && item.core_entity_info) {
    breadcrumbParts.push("Core Entity Information");
    breadcrumbParts.push("Entity Information");
    breadcrumbParts.push("Director");
    const directorName = item.director_data.data.preferred_name
      ? item.director_data.data.preferred_name
      : `${item.director_data.data.name}${item.director_data.data.last_name ? " " + item.director_data.data.last_name : ""}`;
    breadcrumbParts.push(directorName);
  }
  // Add director breadcrumb without core entity info
  else if (item.director_data?.data) {
    breadcrumbParts.push("Core Entity Information");
    breadcrumbParts.push("Director");
    const directorName = item.director_data.data.preferred_name
      ? item.director_data.data.preferred_name
      : `${item.director_data.data.name}${item.director_data.data.last_name ? " " + item.director_data.data.last_name : ""}`;
    breadcrumbParts.push(directorName);
  }
  // Add ad hoc request breadcrumb
  else if (item.request_data) {
    console.log("Request data 123:", item.request_data.data.reason);
    breadcrumbParts.push("Ad Hoc Requests");
    if (item.year) {
      breadcrumbParts.push(item.year);
    }
    if (item.request_data.data.reason) {
      breadcrumbParts.push(item.request_data.data.reason);
    }
    // if (item.request_data.request_id) {
    //   // Extract numeric part from request_id (e.g., "REQ-007" -> "7")
    //   const requestIdMatch = item.request_data.request_id.match(/\d+$/);
    //   const requestNumber = requestIdMatch
    //     ? parseInt(requestIdMatch[0], 10).toString()
    //     : item.request_data.request_id;
    //   breadcrumbParts.push(`Request ${requestNumber}`);
    // }
  }
  // Add process/payslip breadcrumb (payslip, books, EOR, etc. all come through process API)
  else if (
    item.payslip_data?.data?.serviceData?.name ||
    item.process_data?.data?.serviceData?.name
  ) {
    const processData = item.payslip_data?.data || item.process_data?.data;
    breadcrumbParts.push(processData.serviceData.name);
    if (item.year) {
      breadcrumbParts.push(item.year);
    }
    if (processData.name) {
      // Extract period code from start_date (YYYYMM format)
      let processName = processData.name;
      if (processData.start_date) {
        const periodCode =
          new Date(processData.start_date).getFullYear() +
          new Date(processData.start_date).toLocaleDateString("default", {
            month: "2-digit",
          });
        // Only append period code if it's not already in the name
        if (!processName.includes(periodCode)) {
          processName = `${processName} (${periodCode})`;
        }
      }
      breadcrumbParts.push(processName);
    }
  }
  // Add core entity info only
  else if (item.core_entity_info) {
    breadcrumbParts.push("Core Entity Information");
    breadcrumbParts.push("Entity Information");
  }
  console.log("Breadcrumb:", breadcrumbParts.join(" / "));
  return breadcrumbParts.join(" / ");
};

/**
 * Hook to process metadata array and convert ULID IDs to UUIDs
 * Also fetches data from backend for officer, process, shareHolder, and entity
 * Generates breadcrumbs for each metadata item
 *
 * @param {Array} metadataArray - Array of metadata objects to process
 * @returns {Object} { processedData, loading, error }
 */
const useMetadataProcessor = (metadataArray = []) => {
  const [processedData, setProcessedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const processMetadata = useCallback(async () => {
    if (!Array.isArray(metadataArray) || metadataArray.length === 0) {
      setProcessedData([]);
      return;
    }

    setLoading(true);
    setError(null);
    console.log("Metadata Array:", metadataArray);

    try {
      const processedItems = await Promise.all(
        metadataArray.map(async (item) => {
          // Extract metadata from nested metadata object or use item directly
          const metadata = item.metadata || item;
          const processedItem = {
            filename: item.filename || item.name,
            ...metadata,
          };

          // Convert ULID to UUID for officer
          if (metadata.officer) {
            console.log("Officer ULID:", metadata.officer);
            try {
              processedItem.officer_uuid = ulidToUuid(metadata.officer);
              const response = await apiService(
                API_ROUTES_V2.FETCH_OFFICER,
                "GET",
                { replace: { id: processedItem.officer_uuid } },
              );
              processedItem.officer_data = response;
              console.log("Officer data:", processedItem.officer_data);
            } catch (err) {
              console.error("Error fetching officer data:", err);
            }
          }

          // Convert ULID to UUID for payslip or process (both use FETCH_PROCESS API)
          // Payslip, Books, EOR, etc. all come through the process API
          if (metadata.payslip) {
            console.log("Payslip ULID:", metadata.payslip);
            try {
              processedItem.payslip_uuid = ulidToUuid(metadata.payslip);
              const response = await apiService(
                API_ROUTES_V2.FETCH_PROCESS,
                "GET",
                { replace: { id: processedItem.payslip_uuid } },
              );
              processedItem.payslip_data = response;
              console.log("Payslip data:", processedItem.payslip_data);
            } catch (err) {
              console.error("Error fetching payslip data:", err);
            }
          }

          // Convert ULID to UUID for process
          if (metadata.process) {
            console.log("Process ULID:", metadata.process);
            try {
              processedItem.process_uuid = ulidToUuid(metadata.process);
              const response = await apiService(
                API_ROUTES_V2.FETCH_PROCESS,
                "GET",
                { replace: { id: processedItem.process_uuid } },
              );
              processedItem.process_data = response;
              console.log("Process data:", processedItem.process_data);
            } catch (err) {
              console.error("Error fetching process data:", err);
            }
          }

          // Convert ULID to UUID for shareHolder
          if (metadata.shareHolder) {
            console.log("Shareholder ULID:", metadata.shareHolder);
            try {
              processedItem.shareHolder_uuid = ulidToUuid(metadata.shareHolder);
              // Get entity UUID for the shareholder API call
              const entity_id = metadata.entity
                ? ulidToUuid(metadata.entity)
                : "";
              const response = await apiService(
                `${API_ROUTES_V2.FETCH_SHAREHOLDER}`,
                "GET",
                {
                  replace: {
                    id: processedItem.shareHolder_uuid,
                    entity_id: entity_id,
                  },
                },
              );
              processedItem.shareHolder_data = response;
              console.log("Shareholder data:", processedItem.shareHolder_data);
            } catch (err) {
              console.error("Error fetching shareholder data:", err);
            }
          }

          // Convert ULID to UUID for director
          if (metadata.director) {
            console.log("Director ULID:", metadata.director);
            try {
              processedItem.director_uuid = ulidToUuid(metadata.director);
              // Get entity UUID for the director API call
              const entity_id = metadata.entity
                ? ulidToUuid(metadata.entity)
                : "";
              const response = await apiService(
                API_ROUTES_V2.FETCH_DIRECTOR,
                "GET",
                {
                  replace: {
                    id: processedItem.director_uuid,
                    entity_id: entity_id,
                  },
                },
              );
              processedItem.director_data = response;
              console.log("Director data:", processedItem.director_data);
            } catch (err) {
              console.error("Error fetching director data:", err);
            }
          }

          // Convert ULID to UUID for entity (only if core_entity_info exists)
          if (metadata.entity && metadata.core_entity_info) {
            console.log("Entity ULID:", metadata.entity);
            try {
              processedItem.entity_uuid = ulidToUuid(metadata.entity);
              const response = await apiService(
                API_ROUTES_V2.FETCH_ENTITY,
                "GET",
                { replace: { id: processedItem.entity_uuid } },
              );
              processedItem.entity_data = response;
              console.log("Entity data:", processedItem.entity_data);
            } catch (err) {
              console.error("Error fetching entity data:", err);
            }
          }

          // Convert ULID to UUID for ad hoc request (only if request_reason is present)
          if (metadata.request && metadata.request_reason) {
            console.log("Request ULID:", metadata.request);
            try {
              processedItem.request_uuid = ulidToUuid(metadata.request);
              const response = await apiService(
                API_ROUTES_V2.FETCH_REQUEST,
                "GET",
                { replace: { id: processedItem.request_uuid } },
              );
              processedItem.request_data = response;
              console.log("Request data:", processedItem.request_data);
            } catch (err) {
              console.error("Error fetching request data:", err);
            }
          }

          // Generate breadcrumb
          processedItem.breadcrumb = generateBreadcrumb(processedItem);

          return processedItem;
        }),
      );

      // Transform to include breadcrumb at top level and return full document data
      const enrichedItems = metadataArray.map((originalItem, index) => {
        const processedItem = processedItems[index];
        return {
          ...originalItem,
          breadcrumb: processedItem.breadcrumb,
        };
      });

      setProcessedData(enrichedItems);
      setLoading(false);
    } catch (err) {
      const errorMessage = err.message || "Error processing metadata";
      setError(errorMessage);
      setLoading(false);
    }
  }, [metadataArray]);

  // Auto-process when metadata changes
  useEffect(() => {
    processMetadata();
  }, [processMetadata]);

  console.log("Processed Data:", processedData);
  return {
    processedData,
    loading,
    error,
    setLoading,
  };
};

export default useMetadataProcessor;
